import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'signin'; // 'signin' or 'signup'
  const isSignUp = mode === 'signup';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Reset form when switching modes
  useEffect(() => {
    setError(null);
  }, [mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        // Get guestUserId from localStorage for data migration
        const guestUserId = localStorage.getItem('guestUserId');

        const response = await api.register({
          email,
          password,
          firstName,
          lastName,
          guestUserId: guestUserId || undefined
        });

        // Show success message if data was migrated
        if (response.migratedData && (response.migratedData.statements > 0 || response.migratedData.transactions > 0)) {
          console.log(`✅ Successfully migrated ${response.migratedData.statements} statements and ${response.migratedData.transactions} transactions to your account!`);
        }
      } else {
        await api.login(email, password);
      }
      // Redirect to home page after successful login/signup
      navigate('/');
      // Reload to update auth state
      window.location.reload();
    } catch (err: any) {
      console.error(`${isSignUp ? 'Sign up' : 'Login'} failed:`, err);
      setError(err.response?.data?.message || `${isSignUp ? 'Sign up' : 'Login'} failed. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden flex items-center justify-center py-12 px-4">
      {/* Grid Background */}
      <div className="absolute inset-0" style={{
        backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px'
      }}></div>

      {/* Accent Lines */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent"></div>
      <div className="absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-blue-500 to-transparent"></div>

      {/* Login/Signup Card */}
      <div className="relative w-full max-w-md z-10">
        <div className="relative bg-white border-4 border-black p-8">
          <div className="absolute -top-1 -left-1 w-full h-full bg-emerald-500 -z-10"></div>

          {/* Header */}
          <div className="text-center mb-8 pb-6 border-b-4 border-black">
            <h1 className="text-5xl font-black text-black mb-3 tracking-tighter">
              {isSignUp ? 'SIGN UP' : 'SIGN IN'}
            </h1>
            <p className="text-sm font-mono text-gray-600 uppercase tracking-wider">
              {isSignUp ? 'Create account to save your data' : 'Welcome back to DIRHAM'}
            </p>
          </div>

          {/* Toggle Mode Links */}
          <div className="flex justify-center space-x-2 mb-6">
            <a
              href="/login?mode=signin"
              className={`relative px-6 py-2 font-black text-sm border-2 border-black transition-transform hover:translate-x-0.5 hover:-translate-y-0.5 ${
                !isSignUp
                  ? 'bg-black text-white'
                  : 'bg-white text-black'
              }`}
            >
              {!isSignUp && <div className="absolute -top-0.5 -right-0.5 w-full h-full bg-gray-700 -z-10"></div>}
              SIGN IN
            </a>
            <a
              href="/login?mode=signup"
              className={`relative px-6 py-2 font-black text-sm border-2 border-black transition-transform hover:translate-x-0.5 hover:-translate-y-0.5 ${
                isSignUp
                  ? 'bg-black text-white'
                  : 'bg-white text-black'
              }`}
            >
              {isSignUp && <div className="absolute -top-0.5 -right-0.5 w-full h-full bg-gray-700 -z-10"></div>}
              SIGN UP
            </a>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Sign Up Fields */}
            {isSignUp && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono text-gray-700 uppercase tracking-wider mb-2 font-bold">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-black font-bold focus:outline-none focus:ring-4 focus:ring-blue-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-gray-700 uppercase tracking-wider mb-2 font-bold">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-black font-bold focus:outline-none focus:ring-4 focus:ring-blue-300"
                    required
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-xs font-mono text-gray-700 uppercase tracking-wider mb-2 font-bold">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-black font-bold focus:outline-none focus:ring-4 focus:ring-blue-300"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-mono text-gray-700 uppercase tracking-wider mb-2 font-bold">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-black font-bold focus:outline-none focus:ring-4 focus:ring-blue-300"
                required
                minLength={8}
                pattern={isSignUp ? "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$" : undefined}
                title={isSignUp ? "Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number" : undefined}
              />
              {isSignUp && (
                <p className="mt-1 text-xs font-mono text-gray-500">
                  Min. 8 chars, 1 uppercase, 1 lowercase, 1 number
                </p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-500 border-4 border-black flex items-start space-x-3">
                <div className="text-2xl">❌</div>
                <div>
                  <h3 className="font-black text-white uppercase tracking-wider text-sm">Error</h3>
                  <p className="text-white text-xs mt-1 font-mono">{error}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="relative w-full px-8 py-4 bg-black text-white font-black text-lg border-4 border-black transition-transform hover:translate-x-1 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 flex items-center justify-center space-x-3"
            >
              <div className="absolute -top-1 -right-1 w-full h-full bg-gray-700 -z-10"></div>
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-4 border-white"></div>
                  <span>{isSignUp ? 'CREATING ACCOUNT...' : 'SIGNING IN...'}</span>
                </>
              ) : (
                <span>{isSignUp ? '✓ CREATE ACCOUNT' : '→ SIGN IN'}</span>
              )}
            </button>
          </form>

          {/* Test Account Info (only on sign in) */}
          {!isSignUp && (
            <div className="mt-6 p-4 bg-blue-500 border-4 border-black">
              <p className="text-white font-black text-sm mb-2 uppercase tracking-wider">
                🔑 Test Account
              </p>
              <p className="text-white text-xs font-mono">
                Email: testuser@example.com<br />
                Password: Test1234
              </p>
            </div>
          )}

          {/* Back to Home Link */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-sm font-mono text-gray-600 hover:text-black uppercase tracking-wider underline"
            >
              ← Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
